import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Button,
  List,
  Descriptions,
  Avatar,
  Select,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import {
  PlusOutlined,
  ExclamationOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

function Billing() {
  // États pour les soldes
  const [waveBalance, setWaveBalance] = useState(null);
  const [hub2Balance, setHub2Balance] = useState(null);
  const [lafricamobileBalance, setLafricamobileBalance] = useState(null);
  const [lafricasTalkingBalance, setLafricasTalkingBalance] = useState(null);
  const [reloadlyBalance, setReloadlyBalance] = useState(null);
  const [cinetpayBalance, setCinetpayBalance] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("CI");
  const [loading, setLoading] = useState(true);

  // Liste des drapeaux associés aux codes pays
  const countryFlags = {
    BF: "/flag/bf.png", // Burkina Faso
    BJ: "/flag/bj.png", // Bénin
    CD: "/flag/cd.png", // RD Congo
    CI: "/flag/ci.svg", // Côte d'Ivoire
    CM: "/flag/cm.png", // Cameroun
    GN: "/flag/gn.png", // Guinée
    ML: "/flag/ml.png", // Mali
    SN: "/flag/sn.png", // Sénégal
    TG: "/flag/tg.png", // Togo
  };

  // Icônes SVG pour un design cohérent
  const wifiIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="25"
      viewBox="0 0 22.5 20.625"
    >
      <g id="wifi" transform="translate(0.75 0.75)">
        <circle
          id="Oval"
          cx="1.5"
          cy="1.5"
          r="1.5"
          transform="translate(9 16.875)"
          fill="#fff"
        />
        <path
          id="Path"
          d="M0,1.36a6.377,6.377,0,0,1,7.5,0"
          transform="translate(6.75 11.86)"
          fill="none"
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          strokeWidth="1.5"
        />
        <path
          id="Path-2"
          d="M14.138,2.216A12.381,12.381,0,0,0,0,2.216"
          transform="translate(3.431 6)"
          fill="none"
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          strokeWidth="1.5"
        />
        <path
          id="Path-3"
          d="M0,3.294a18.384,18.384,0,0,1,21,0"
          fill="none"
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );

  const pencilIcon = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.5858 3.58579C14.3668 2.80474 15.6332 2.80474 16.4142 3.58579C17.1953 4.36683 17.1953 5.63316 16.4142 6.41421L15.6213 7.20711L12.7929 4.37868L13.5858 3.58579Z"
        fill="#9CA3AF"
      />
      <path
        d="M11.3787 5.79289L3 14.1716V17H5.82842L14.2071 8.62132L11.3787 5.79289Z"
        fill="#9CA3AF"
      />
    </svg>
  );

  const downloadIcon = (
    <svg
      width="15"
      height="15"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 17C3 16.4477 3.44772 16 4 16H16C16.5523 16 17 16.4477 17 17C17 17.5523 16.5523 18 16 18H4C3.44772 18 3 17.5523 3 17ZM6.29289 9.29289C6.68342 8.90237 7.31658 8.90237 7.70711 9.29289L9 10.5858L9 3C9 2.44772 9.44771 2 10 2C10.5523 2 11 2.44771 11 3L11 10.5858L12.2929 9.29289C12.6834 8.90237 13.3166 8.90237 13.7071 9.29289C14.0976 9.68342 14.0976 10.3166 13.7071 10.7071L10.7071 13.7071C10.5196 13.8946 10.2652 14 10 14C9.73478 14 9.48043 13.8946 9.29289 13.7071L6.29289 10.7071C5.90237 10.3166 5.90237 9.68342 6.29289 9.29289Z"
        fill="#111827"
      />
    </svg>
  );

  // Fonctions de récupération des soldes
  const fetchWaveBalance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/balance/wave`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setWaveBalance(data);
    } catch (error) {
      console.error("Erreur Wave :", error);
      message.error("Erreur lors de la récupération du solde Wave");
    }
  };

  const fetchHub2Balance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance/hub2`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setHub2Balance(data);
    } catch (error) {
      console.error("Erreur Hub2 :", error);
      message.error("Erreur lors de la récupération du solde Hub2");
    }
  };

  const fetchReloadlyBalance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance/reloadly/balance`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setReloadlyBalance(data);
    } catch (error) {
      console.error("Erreur Reloadly :", error);
      message.error("Erreur lors de la récupération du solde Reloadly");
    }
  };

  /*
  const fetchCinetpayBalance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance/cinetpay/balance`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setCinetpayBalance(data);
    } catch (error) {
      console.error("Erreur Cinetpay :", error);
      message.error("Erreur lors de la récupération du solde Cinetpay");
    }
  }; */

  const fetchLafricaMobileBalance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance/lafricamobile`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setLafricamobileBalance(data);
    } catch (error) {
      console.error("Erreur Wave :", error);
      message.error("Erreur lors de la récupération du solde Wave");
    }
  };

  const fetchLafricasTalkingBalance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance/africastalking`,
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setLafricasTalkingBalance(data);
    } catch (error) {
      console.error("Erreur Wave :", error);
      message.error("Erreur lors de la récupération du solde Wave");
    }
  };

  useEffect(() => {
    const fetchAllBalances = async () => {
      setLoading(true);
      await Promise.all([
        //fetchWaveBalance(),
        fetchHub2Balance(),
        fetchReloadlyBalance(),
        //fetchCinetpayBalance(),
        fetchLafricaMobileBalance(),
        fetchLafricasTalkingBalance(),
      ]);
      setLoading(false);
    };

    fetchAllBalances();
  }, []);

  console.log("lafrica", lafricamobileBalance);

  // Calcul du solde par pays pour Cinetpay
  const countryBalance =
    cinetpayBalance?.data?.countryBalance?.[selectedCountry]?.available || 0;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text>Chargement des soldes...</Text>
        </div>
      </div>
    );
  }

  return (
    <>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={16}>
          <Row gutter={[24, 0]}>
            {/* Hub2 Balance */}
            <Col xs={20} md={12} className="mb-24">
              <Card bordered={false} className="widget-2 h-full">
                <Statistic
                  title={
                    <>
                      <div
                        className="icon "
                        style={{
                          background: "#f59e0b",
                        }}
                      >
                        <img
                          src="/hub2.png"
                          alt="Hub2"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <h6>Hub2</h6>
                      <p>Total Balance</p>
                    </>
                  }
                  value={`${
                    (hub2Balance?.collectionAccount?.[0]?.availableBalance ||
                      0) +
                    (hub2Balance?.transferAccount?.[0]?.availableBalance || 0)
                  } XOF`}
                  prefix={<PlusOutlined />}
                />
              </Card>
            </Col>
            {/* Africas Talking */}
            <Col xs={20} md={12} className="mb-24">
              <Card bordered={false} className="widget-2 h-full">
                <Statistic
                  title={
                    <>
                      <div
                        className="icon "
                        style={{
                          background: "#99c213",
                        }}
                      >
                        <img
                          src="/AT.png"
                          alt="Africas Talking"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: "10%",
                          }}
                        />
                      </div>
                      <h6>Africas Talking</h6>
                      <p>Total Balance</p>
                    </>
                  }
                  value={`${
                    lafricasTalkingBalance?.UserData?.balance || 0
                  } XOF`}
                  prefix={<PlusOutlined />}
                />
              </Card>
            </Col>
            {/* Méthodes de paiement */}
            <Col xs={24} className="mb-24">
              <Card
                className="header-solid h-full ant-card-p-0"
                title={
                  <Row
                    gutter={[24, 0]}
                    className="ant-row-flex ant-row-flex-middle"
                  >
                    <Col xs={24} md={12}>
                      <h6 className="font-semibold m-0">
                        Méthodes de Paiement
                      </h6>
                    </Col>
                    <Col xs={24} md={12} className="d-flex">
                      <Button type="primary">SERVICES</Button>
                    </Col>
                  </Row>
                }
              >
                <Row gutter={[16, 16]}>
                  {/* Wave */}
                  <Col span={6} md={2}>
                    <img
                      src="/wave.png"
                      alt="Wave"
                      style={{
                        width: "100%",
                        height: "100%",
                        marginBottom: "8px",
                        objectFit: "contain",
                      }}
                    />
                  </Col>
                  {/* Hub2 */}
                  <Col span={6} md={2}>
                    <img
                      src="/hub2.png"
                      alt="Hub2"
                      style={{
                        width: "100%",
                        height: "100%",
                        marginBottom: "8px",
                        objectFit: "contain",
                      }}
                    />
                  </Col>
                  <Col span={6} md={2}>
                    <img
                      src="/AT.png"
                      alt="Africa's Talking"
                      style={{
                        width: "100%",
                        height: "100%",
                        marginBottom: "8px",
                        objectFit: "contain",
                        background: "#ffff",
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        {/* Informations des Comptes API */}
        <Col span={24} md={16} className="mb-24">
          <Card
            className="header-solid h-full"
            bordered={false}
            title={
              <h6 className="font-semibold m-0">Détails des Comptes API</h6>
            }
            bodyStyle={{ paddingTop: "0" }}
          >
            <Row gutter={[24, 24]}>
              {/* Compte Hub2 */}
              <Col span={24}>
                <Card className="card-billing-info" bordered={false}>
                  <div className="col-info">
                    <Descriptions title="Hub2 Payment Gateway">
                      <Descriptions.Item label="Compte Collection" span={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <img
                            src="/hub2.png"
                            alt="Hub2"
                            style={{ width: "24px", height: "18px" }}
                          />
                          <span>
                            {hub2Balance?.collectionAccount?.[0]
                              ?.availableBalance || 0}{" "}
                            {hub2Balance?.collectionAccount?.[0]?.currency ||
                              "XOF"}
                          </span>
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="Compte Transfer" span={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <img
                            src="/hub2.png"
                            alt="Hub2"
                            style={{ width: "24px", height: "18px" }}
                          />
                          <span>
                            {hub2Balance?.transferAccount?.[0]
                              ?.availableBalance || 0}{" "}
                            {hub2Balance?.transferAccount?.[0]?.currency ||
                              "XOF"}
                          </span>
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="Dernière MAJ" span={3}>
                        {hub2Balance?.collectionAccount?.[0]?.date
                          ? new Date(
                              hub2Balance.collectionAccount[0].date,
                            ).toLocaleDateString("fr-FR")
                          : "Non disponible"}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                  <div className="col-action">
                    <Button type="link" className="darkbtn">
                      {pencilIcon} ACTUALISER
                    </Button>
                  </div>
                </Card>
              </Col>

              {/** Compte Africa's Talking */}
              <Col span={24}>
                <Card className="card-billing-info" bordered={false}>
                  <div className="col-info">
                    <Descriptions title="Africa's Talking solde">
                      <Descriptions.Item label="Compte principal" span={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <img
                            src="/AT.png"
                            alt="Africa's Talking"
                            style={{
                              width: "24px",
                              height: "18px",
                              background: "#007BFF",
                            }}
                          />
                          <span>
                            {lafricasTalkingBalance.UserData.balance || 0} XOF
                          </span>
                        </div>
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions title="Pays de distribution">
                      <Descriptions.Item label="Nom du pays" span={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span>Cote d'Ivoire</span>
                        </div>
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                  <div className="col-action">
                    <Button type="link" className="darkbtn">
                      {pencilIcon} ACTUALISER
                    </Button>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default Billing;
